import { useEffect, useMemo, useState } from "react";
import { Send, Bell } from "lucide-react";
import { toast } from "sonner";

import AdminPageHeader from "@/components/layout/AdminPageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SCREENS } from "@/constants/testIds";
import {
  crearNotificacion, listarNotificaciones, hidratarNotificaciones, TIPOS_NOTIF, DESTINATARIOS,
} from "@/data/adminNotifsStore";

const TIPO_LABEL = Object.fromEntries(TIPOS_NOTIF.map((t) => [t.id, t.label]));
const DEST_LABEL = Object.fromEntries(DESTINATARIOS.map((d) => [d.id, d.label]));

export default function AdminNotificaciones() {
  const [tick, setTick] = useState(0);
  const [form, setForm] = useState({ tipo: "GENERAL", destinatario: "TODOS", titulo: "", mensaje: "" });
  const [saving, setSaving] = useState(false);
  const historial = useMemo(() => listarNotificaciones(), [tick]);

  useEffect(() => {
    hidratarNotificaciones().then(() => setTick((t) => t + 1));
  }, []);

  const enviar = async () => {
    if (!form.titulo.trim() || !form.mensaje.trim()) {
      toast.error("Título y mensaje son obligatorios.");
      return;
    }
    setSaving(true);
    const n = await crearNotificacion({ ...form });
    setSaving(false);
    if (!n) {
      toast.error("Título y mensaje son obligatorios.");
      return;
    }
    if (n.error) {
      const err = n.error;
      const msg =
        err === "network"
          ? "Sin conexión con el servidor. Intenta de nuevo."
          : typeof err === "string" && err.trim()
            ? err
            : "No se pudo guardar la notificación.";
      toast.error(msg);
      return;
    }
    toast.success("Notificación guardada. Se enviará cuando el servicio esté disponible.");
    setForm({ tipo: "GENERAL", destinatario: "TODOS", titulo: "", mensaje: "" });
    setTick((t) => t + 1);
  };

  return (
    <div data-testid={SCREENS.adminNotificaciones} className="flex flex-col gap-8">
      <AdminPageHeader eyebrow="Portal administrativo" title="Centro de notificaciones" description="Compone y guarda notificaciones para socios. El envío real se habilitará más adelante." />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6">
        <section className="rounded-2xl border hairline bg-card p-6 flex flex-col gap-4">
          <header className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/5 dark:bg-accent/10 text-primary dark:text-accent ring-1 ring-primary/20 dark:ring-accent/30 grid place-items-center">
              <Send className="w-4 h-4" strokeWidth={1.7} />
            </div>
            <div>
              <h3 className="font-heading text-lg leading-tight">Nueva notificación</h3>
              <p className="text-xs text-muted-foreground">Sólo se guarda; no se envía en este sprint.</p>
            </div>
          </header>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((s) => ({ ...s, tipo: v }))}>
                <SelectTrigger data-testid="notif-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_NOTIF.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Destinatarios</Label>
              <Select value={form.destinatario} onValueChange={(v) => setForm((s) => ({ ...s, destinatario: v }))}>
                <SelectTrigger data-testid="notif-destinatario"><SelectValue /></SelectTrigger>
                <SelectContent>{DESTINATARIOS.map((d) => <SelectItem key={d.id} value={d.id}>{d.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Título</Label>
              <Input data-testid="notif-titulo" value={form.titulo} onChange={(e) => setForm((s) => ({ ...s, titulo: e.target.value }))} placeholder="Ej. Nuevos boletos disponibles" />
            </div>
            <div className="col-span-2">
              <Label>Mensaje</Label>
              <Textarea data-testid="notif-mensaje" rows={5} value={form.mensaje} onChange={(e) => setForm((s) => ({ ...s, mensaje: e.target.value }))} className="resize-none" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button data-testid="notif-guardar" onClick={enviar} disabled={saving}>
              <Bell className="w-4 h-4 mr-2" /> {saving ? "Guardando…" : "Guardar notificación"}
            </Button>
          </div>
        </section>

        <aside className="rounded-2xl border hairline bg-card p-6 flex flex-col gap-3 h-fit">
          <h3 className="font-heading text-base">Historial</h3>
          {historial.length === 0 ? (
            <p data-testid="notif-empty" className="text-sm text-muted-foreground">Aún no hay notificaciones registradas.</p>
          ) : (
            <ul className="flex flex-col gap-2 max-h-[500px] overflow-y-auto" data-testid="notif-historial">
              {historial.map((n) => {
                const d = n.fechaCreacion ? new Date(n.fechaCreacion) : null;
                const estado = n.enviada ? "Enviada" : "Guardada";
                const estadoTone = n.enviada
                  ? "text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border-emerald-500/40"
                  : "text-muted-foreground bg-muted border-border/70";
                return (
                  <li key={n.id} data-testid={`notif-item-${n.id}`} className="rounded-xl border hairline bg-background p-3">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-widest text-accent">{TIPO_LABEL[n.tipo] || n.tipo}</span>
                      <span data-testid={`notif-estado-${n.id}`} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${estadoTone}`}>
                        {estado}
                      </span>
                    </div>
                    <p className="mt-1 text-sm font-medium leading-tight">{n.titulo}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{n.mensaje}</p>
                    <div className="mt-2 pt-2 border-t hairline flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground">
                      {d && (
                        <>
                          <span>📅 {d.toLocaleDateString("es-MX")}</span>
                          <span>🕐 {d.toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>
                        </>
                      )}
                      <span>👤 {n.autor}</span>
                      <span>→ {DEST_LABEL[n.destinatario] || n.destinatario}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
